/**
 * Iter51 — Native safe-area inset reader.
 *
 * On Capacitor 7 Android (especially with edge-to-edge mode), CSS
 * `env(safe-area-inset-bottom)` reliably returns 0 even when the OS
 * has reserved space for a gesture-nav pill or 3-button nav bar. This
 * caused the Coach Tip booster button to render directly over the
 * Android gesture-pill touch region, eating taps and triggering the
 * "close app" gesture instead.
 *
 * This module queries the native `capacitor-plugin-safe-area` plugin
 * for the REAL insets (in CSS pixels), then mirrors them onto the
 * `<html>` element as CSS custom properties so the rest of the app
 * can use `var(--safe-bottom, 48px)` instead of the broken
 * `env(safe-area-inset-bottom)`.
 *
 * The 48 px fallback is intentional: even if the plugin fails to
 * initialise we still leave a safe gap so the bug never reappears.
 *
 * Listens to `safeAreaChanged` so orientation/IME changes update the
 * vars live (the plugin re-emits insets when the WebView resizes).
 */
import { Capacitor } from "@capacitor/core";

const FALLBACK_PX = { top: 24, right: 0, bottom: 48, left: 0 };
const VAR_NAMES = {
  top: "--safe-top",
  right: "--safe-right",
  bottom: "--safe-bottom",
  left: "--safe-left",
};

function applyInsets(insets) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const side of ["top", "right", "bottom", "left"]) {
    const px = Math.max(0, Math.round(insets?.[side] ?? FALLBACK_PX[side]));
    root.style.setProperty(VAR_NAMES[side], `${px}px`);
  }
}

// Apply fallback immediately so the CSS never sees a moment with the
// variables unset (otherwise the very first paint would still hug the
// system bar).
applyInsets(FALLBACK_PX);

let initialised = false;

/**
 * Initialise safe-area insets. Safe to call multiple times — only the
 * first call subscribes; subsequent calls just re-fetch.
 */
export async function initSafeAreaInsets() {
  // The plugin only ships native code for ios/android. On web (the
  // preview) leave the fallback in place — the dev browser handles
  // safe-area via env() on its own.
  if (!Capacitor.isNativePlatform()) return;

  try {
    // Dynamic import so the web preview doesn't bundle the native plugin.
    const mod = await import("capacitor-plugin-safe-area");
    const SafeArea = mod.SafeArea;
    if (!SafeArea) return;

    const { insets } = await SafeArea.getSafeAreaInsets();
    applyInsets(insets);

    if (!initialised) {
      initialised = true;
      try {
        // Re-apply on orientation / IME-driven changes.
        await SafeArea.addListener("safeAreaChanged", ({ insets: next }) => {
          applyInsets(next);
        });
      } catch (_e) {
        // listener API differs across versions; ignore if not available
      }
    }
  } catch (e) {
    // Fail loudly in dev, silently in prod.
    if (process.env.NODE_ENV !== "production") {
      console.warn("[safeArea] init failed:", e);
    }
  }
}
